// src/pages/Admin/CoursAgenda/components/CoursAgendaWeekBoard.jsx

import React, { useMemo, useState } from "react";

import {
  FaCalendarAlt,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

import CoursAgendaCourseCard from "./CoursAgendaCourseCard";

import {
  addDays,
  formatShortDate,
  formatShortDay,
  formatWeekLabel,
  isSameLocalDay,
  startOfISOWeek,
  ymdLocal,
} from "../utils/coursAgendaUtils";

const CoursAgendaWeekBoard = ({
  cours = [],
  onOpen,
}) => {
  const [weekOffset, setWeekOffset] =
    useState(0);

  const today = new Date();

  const weekStart = useMemo(() => {
    const currentWeek =
      startOfISOWeek(new Date());

    return addDays(
      currentWeek,
      weekOffset * 7
    );
  }, [weekOffset]);

  const days = useMemo(
    () =>
      Array.from(
        { length: 7 },
        (_, index) =>
          addDays(weekStart, index)
      ),
    [weekStart]
  );

  const coursByDay = useMemo(() => {
    const map = {};

    days.forEach((day) => {
      map[ymdLocal(day)] = [];
    });

    cours.forEach((item) => {
      const dateKey =
        item.date_cours?.slice(0, 10);

      if (!dateKey || !map[dateKey]) {
        return;
      }

      map[dateKey].push(item);
    });

    Object.values(map).forEach(
      (items) => {
        items.sort((a, b) => {
          const startA =
            a.heure_debut || "";

          const startB =
            b.heure_debut || "";

          return startA.localeCompare(
            startB
          );
        });
      }
    );

    return map;
  }, [cours, days]);

  const weekCourseCount = useMemo(
    () =>
      Object.values(coursByDay).reduce(
        (total, items) =>
          total + items.length,
        0
      ),
    [coursByDay]
  );

  return (
    <section
      className="
        overflow-hidden
        rounded-2xl
        border border-slate-200
        bg-slate-50
        shadow-sm
        dark:border-slate-800
        dark:bg-slate-950/50
      "
    >
      {/* Navigation semaine */}
      <div className="flex flex-col gap-4 border-b border-slate-200 bg-white px-4 py-4 dark:border-slate-800 dark:bg-slate-900 sm:flex-row sm:items-center sm:justify-between sm:px-5">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-500 dark:text-slate-400">
            <FaCalendarAlt className="text-red-500" />

            Planning hebdomadaire
          </div>

          <h3 className="mt-1 text-lg font-bold capitalize text-slate-950 dark:text-white sm:text-xl">
            {formatWeekLabel(weekStart)}
          </h3>

          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {weekCourseCount} cours
            {weekCourseCount > 1
              ? " affichés"
              : " affiché"}
          </p>
        </div>

        <div className="grid grid-cols-3 gap-2 sm:flex">
          <button
            type="button"
            onClick={() =>
              setWeekOffset(
                (value) => value - 1
              )
            }
            className="
              flex items-center justify-center gap-2
              rounded-xl
              border border-slate-200
              bg-white
              px-3 py-2.5
              text-sm font-semibold
              text-slate-700
              transition
              hover:bg-slate-100
              dark:border-slate-700
              dark:bg-slate-950
              dark:text-slate-200
              dark:hover:bg-slate-800
            "
          >
            <FaChevronLeft />

            <span className="hidden sm:inline">
              Précédente
            </span>
          </button>

          <button
            type="button"
            onClick={() =>
              setWeekOffset(0)
            }
            className="
              rounded-xl
              bg-slate-900
              px-3 py-2.5
              text-sm font-semibold
              text-white
              transition
              hover:bg-slate-700
              dark:bg-white
              dark:text-slate-950
              dark:hover:bg-slate-200
            "
          >
            Aujourd'hui
          </button>

          <button
            type="button"
            onClick={() =>
              setWeekOffset(
                (value) => value + 1
              )
            }
            className="
              flex items-center justify-center gap-2
              rounded-xl
              border border-slate-200
              bg-white
              px-3 py-2.5
              text-sm font-semibold
              text-slate-700
              transition
              hover:bg-slate-100
              dark:border-slate-700
              dark:bg-slate-950
              dark:text-slate-200
              dark:hover:bg-slate-800
            "
          >
            <span className="hidden sm:inline">
              Suivante
            </span>

            <FaChevronRight />
          </button>
        </div>
      </div>

      {/* Jours */}
      <div
        className="
          grid
          grid-cols-1
          gap-px
          bg-slate-200
          dark:bg-slate-800
          md:grid-cols-2
          xl:grid-cols-4
          2xl:grid-cols-7
        "
      >
        {days.map((day) => {
          const key = ymdLocal(day);

          const items =
            coursByDay[key] || [];

          const isToday =
            isSameLocalDay(
              day,
              today
            );

          return (
            <article
              key={key}
              className="
                min-h-[210px]
                bg-slate-50
                p-3
                dark:bg-slate-950
              "
            >
              {/* Entête jour */}
              <header
                className={`
                  mb-3
                  rounded-xl
                  border
                  px-3 py-2.5
                  ${
                    isToday
                      ? "border-red-200 bg-red-50 dark:border-red-900/70 dark:bg-red-950/30"
                      : "border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900"
                  }
                `}
              >
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <h4
                      className={`
                        text-sm
                        font-bold
                        capitalize
                        ${
                          isToday
                            ? "text-red-700 dark:text-red-300"
                            : "text-slate-900 dark:text-white"
                        }
                      `}
                    >
                      {formatShortDay(day)}
                    </h4>

                    <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                      {formatShortDate(day)}
                    </p>
                  </div>

                  {items.length > 0 && (
                    <span
                      className="
                        flex h-7 min-w-7 items-center
                        justify-center
                        rounded-full
                        bg-slate-100
                        px-2
                        text-xs font-bold
                        text-slate-600
                        dark:bg-slate-800
                        dark:text-slate-300
                      "
                    >
                      {items.length}
                    </span>
                  )}
                </div>

                {isToday && (
                  <div className="mt-2 text-[10px] font-bold uppercase tracking-widest text-red-600 dark:text-red-400">
                    Aujourd'hui
                  </div>
                )}
              </header>

              {/* Cours */}
              {items.length > 0 ? (
                <div className="space-y-3">
                  {items.map((item) => (
                    <CoursAgendaCourseCard
                      key={item.id}
                      cours={item}
                      onOpen={onOpen}
                    />
                  ))}
                </div>
              ) : (
                <div
                  className="
                    flex min-h-[110px]
                    items-center justify-center
                    rounded-xl
                    border border-dashed
                    border-slate-200
                    px-4
                    text-center
                    text-xs
                    text-slate-400
                    dark:border-slate-800
                    dark:text-slate-600
                  "
                >
                  Aucun cours
                </div>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
};

export default CoursAgendaWeekBoard;